import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiErr } from "../../lib/api";
import { Mail, Lock, Eye, EyeOff, ShieldCheck, LogIn, KeyRound, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("creds"); // creds | code
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");

  useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

  useEffect(() => {
    if (sessionStorage.getItem("beg_session_expired")) {
      setNotice("Votre session a expiré, veuillez vous reconnecter.");
      sessionStorage.removeItem("beg_session_expired");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfaRequired) {
        setSentTo(res.sentTo || "");
        setStep("code");
      } else {
        nav("/admin");
      }
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(email, password, code.trim());
      nav("/admin");
    } catch (e2) { setErr(apiErr(e2)); }
    finally { setLoading(false); }
  };

  const backToCreds = () => { setStep("creds"); setCode(""); setErr(""); };

  const field = "w-full rounded-xl border border-ink-400/20 bg-cream/40 pl-11 pr-4 py-3.5 text-sm text-ink-900 placeholder:text-ink-400 outline-none transition-all focus:border-brand focus:bg-white focus:ring-2 focus:ring-brand/20";

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-brand-900">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 0%, rgba(20,128,179,0.35) 0%, rgba(15,58,82,0) 70%)" }} />
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-brand/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-[28px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] ring-1 ring-black/5 p-8 sm:p-10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/header-logo-blue.png"
              alt="Domaine de la Bégaudière"
              className="h-16 w-auto object-contain"
              data-testid="admin-login-logo"
            />
          </div>

          <h1 className="font-heading text-3xl text-ink-900 text-center tracking-tight">
            {step === "code" ? "Vérification" : "Espace administration"}
          </h1>
          <p className="text-ink-400 text-center text-sm mt-1 mb-8">Domaine de la Bégaudière</p>

          {step === "creds" ? (
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            {notice && (
              <p className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3" data-testid="session-expired-notice">
                {notice}
              </p>
            )}

            <div className="relative">
              <Mail size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" />
              <input type="email" placeholder="Adresse e-mail" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" className={field} data-testid="admin-email" />
            </div>

            <div className="relative">
              <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" />
              <input type={showPwd ? "text" : "password"} placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className={`${field} pr-11`} data-testid="admin-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-brand transition-colors" data-testid="admin-toggle-password">
                {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            {err && (
              <p className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5" data-testid="admin-error">{err}</p>
            )}

            <button type="submit" disabled={loading} className="group w-full rounded-full bg-brand text-white py-3.5 font-medium hover:bg-brand-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-brand/25" data-testid="admin-login-submit">
              {loading ? "Connexion…" : (<><LogIn size={17} className="transition-transform group-hover:translate-x-0.5" /> Se connecter</>)}
            </button>
          </form>
          ) : (
          <form onSubmit={submitCode} className="space-y-4" data-testid="admin-2fa-form">
            <p className="text-sm text-ink-600 text-center leading-relaxed">
              Un code de connexion à 6 chiffres vient d'être envoyé
              {sentTo ? <> à <b className="text-ink-900">{sentTo}</b></> : " à votre adresse e-mail de sécurité"}.
              <br />Saisissez-le pour continuer.
            </p>

            <div className="relative">
              <KeyRound size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-400" />
              <input
                inputMode="numeric" maxLength={6} autoFocus
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className={`${field} text-center tracking-[0.4em] text-lg`}
                data-testid="admin-2fa-code"
              />
            </div>

            {err && (
              <p className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5" data-testid="admin-error">{err}</p>
            )}

            <button type="submit" disabled={loading || code.length < 6} className="w-full rounded-full bg-brand text-white py-3.5 font-medium hover:bg-brand-600 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-brand/25" data-testid="admin-2fa-submit">
              {loading ? "Vérification…" : (<><ShieldCheck size={17} /> Vérifier et se connecter</>)}
            </button>

            <button type="button" onClick={backToCreds} className="w-full text-ink-400 hover:text-brand text-sm inline-flex items-center justify-center gap-1.5 transition-colors" data-testid="admin-2fa-back">
              <ArrowLeft size={14} /> Retour
            </button>
          </form>
          )}

          <div className="mt-7 flex items-center justify-center gap-1.5 text-ink-400 text-xs">
            <ShieldCheck size={13} className="text-brand/70" />
            Connexion sécurisée · accès réservé
          </div>
        </div>
      </div>
    </div>
  );
}
